import { useEffect, useState } from 'react';

export type AuditIssueDetailItem = {
  key: string;
  title: string;
  resultLabel: string;
  resultClassName: string;
  whatItMeans: string[];
  fixes: string[];
  auditedPaths?: string[];
  affectedPaths?: string[];
  partialPaths?: string[];
  failedPaths?: string[];
  evidence?: string[];
  compliance?: string | null;
};

const formatAuditPathLabel = (path: string) => path === '/' ? 'index' : path;

export function AuditIssueDetailModal({
  item,
  onClose,
}: {
  item: AuditIssueDetailItem | null;
  onClose: () => void;
}) {
  const [renderedItem, setRenderedItem] = useState<AuditIssueDetailItem | null>(item);
  const [isVisible, setIsVisible] = useState(Boolean(item));
  const [isAnimating, setIsAnimating] = useState(Boolean(item));

  useEffect(() => {
    let frameA = 0;
    let frameB = 0;
    let timer = 0;

    if (item) {
      setRenderedItem(item);
      setIsVisible(true);
      frameA = window.requestAnimationFrame(() => {
        frameB = window.requestAnimationFrame(() => setIsAnimating(true));
      });
    } else if (renderedItem) {
      setIsAnimating(false);
      timer = window.setTimeout(() => {
        setIsVisible(false);
        setRenderedItem(null);
      }, 120);
    }

    return () => {
      if (frameA) window.cancelAnimationFrame(frameA);
      if (frameB) window.cancelAnimationFrame(frameB);
      if (timer) window.clearTimeout(timer);
    };
  }, [item, renderedItem]);

  useEffect(() => {
    if (!isVisible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose]);

  if (!isVisible || !renderedItem) return null;

  return (
    <div className={`rename-modal is-visible search-detail-modal-shell ${isAnimating ? 'is-open' : 'is-closing'}`}>
      <div className="white-background search-detail-modal">
        <div className="modal-close" onClick={onClose} style={{ cursor: 'pointer' }}>
          <div className="rotated-text">+</div>
        </div>

        <div className="search-detail-header">
          <div className="search-detail-header-top">
            <div className="text-size-medium text-weight-semibold">Audit result</div>
            <div className={renderedItem.resultClassName}>
              <div>{renderedItem.resultLabel}</div>
            </div>
          </div>
          <div className="spacer-xxtiny"></div>
          <div className="text-size-regular text-weight-regular text-colour-dark-brown">{renderedItem.title}</div>
          <div className="search-detail-divider"></div>
        </div>

        <div className="search-detail-scroll">
          <div className="search-rendered-content-shell insight-detail-content-shell">
            <div className="insight-detail-section">
              <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">What this result means</div>
              <div className="insights-bullet-list">
                {renderedItem.whatItMeans.map((itemText, index) => (
                  <div key={`${renderedItem.key}-meaning-${index}`} className="insights-bullet-item">
                    <div className="insights-bullet-dot"></div>
                    <div className="text-size-small text-colour-dark-brown">{itemText}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="search-render-divider"></div>
            <div className="insight-detail-section">
              <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">How to fix this issue</div>
              <div className="insights-bullet-list">
                {renderedItem.fixes.map((itemText, index) => (
                  <div key={`${renderedItem.key}-fix-${index}`} className="insights-bullet-item">
                    <div className="insights-bullet-dot"></div>
                    <div className="text-size-small text-colour-dark-brown">{itemText}</div>
                  </div>
                ))}
              </div>
            </div>

            {renderedItem.affectedPaths && renderedItem.affectedPaths.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">
                    Affected pages <span className="number-pill">{renderedItem.affectedPaths.length}</span>
                  </div>
                  <div className="insight-detail-prompt-list">
                    {renderedItem.affectedPaths.map((path, index) => (
                      <div key={`${renderedItem.key}-affected-${index}`} className="insight-detail-prompt-pill">
                        <span className="text-size-xsmall text-colour-dark-brown">{formatAuditPathLabel(path)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {renderedItem.failedPaths && renderedItem.failedPaths.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">
                    Failed pages <span className="number-pill">{renderedItem.failedPaths.length}</span>
                  </div>
                  <div className="insight-detail-prompt-list">
                    {renderedItem.failedPaths.map((path, index) => (
                      <div key={`${renderedItem.key}-failed-${index}`} className="insight-detail-prompt-pill">
                        <span className="text-size-xsmall text-colour-dark-brown">{formatAuditPathLabel(path)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {renderedItem.partialPaths && renderedItem.partialPaths.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">
                    Pages needing improvement <span className="number-pill">{renderedItem.partialPaths.length}</span>
                  </div>
                  <div className="insight-detail-prompt-list">
                    {renderedItem.partialPaths.map((path, index) => (
                      <div key={`${renderedItem.key}-partial-${index}`} className="insight-detail-prompt-pill">
                        <span className="text-size-xsmall text-colour-dark-brown">{formatAuditPathLabel(path)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {renderedItem.auditedPaths && renderedItem.auditedPaths.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">
                    Pages audited <span className="number-pill">{renderedItem.auditedPaths.length}</span>
                  </div>
                  <div className="insight-detail-prompt-list">
                    {renderedItem.auditedPaths.map((path, index) => (
                      <div key={`${renderedItem.key}-audited-${index}`} className="insight-detail-prompt-pill">
                        <span className="text-size-xsmall text-colour-dark-brown">{formatAuditPathLabel(path)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {renderedItem.evidence && renderedItem.evidence.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">Audit evidence</div>
                  <div className="insights-bullet-list">
                    {renderedItem.evidence.map((itemText, index) => (
                      <div key={`${renderedItem.key}-evidence-${index}`} className="insights-bullet-item">
                        <div className="insights-bullet-dot"></div>
                        <div className="text-size-small text-colour-dark-brown">{itemText}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {renderedItem.compliance ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown audit-detail-section-title">Compliance reference</div>
                  <div className="text-size-small text-colour-dark-brown">{renderedItem.compliance}</div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="close-layer" onClick={onClose}></div>
    </div>
  );
}
